import { memo, useState, useCallback } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import dot from 'utils/dot';
import { recalculateNodePositions } from '../../utils';
import { AutomationBuilderCanvasProvider } from './AutomationBuilderCanvasContext';
import AutomationBuilderToolbar from './AutomationBuilderToolbar';
import AutomationBuilderCanvasSidePanel from './AutomationBuilderCanvasSidePanel';
import NodeLayouter from './components/NodesLayouter';
import AutomationBuilderCanvasFlow from './AutomationBuilderCanvasFlow';

import '@xyflow/react/dist/style.css';

const transformDataToNodes = (data) => {
    if (!data || !data.scene) return [];

    const nodes = [];

    const processNode = (node) => {
        nodes.push({
            id: node.id,
            name: node.name,
            position: { x: 0, y: 0 },
            type: node.type,
            data: { ...node.parameters },
        });

        if (node.next && node.next.length > 0) {
            node.next.forEach((nextNode) => processNode(nextNode, nextNode.id));
        }
    };

    processNode(data.scene, data.scene.id);

    return nodes;
};

const transformDataToEdges = (data) => {
    if (!data || !data.scene) return [];

    const edges = [];

    const processNodeForEdges = (node) => {
        const sourceId = node.id;

        if (node.next && node.next.length > 0) {
            node.next.forEach((nextNode) => {
                edges.push({
                    id: `edge-${sourceId}-${nextNode.id}`,
                    source: sourceId,
                    target: nextNode.id,
                    type: 'buttonedge',
                });
                processNodeForEdges(nextNode);
            });
        }
    };

    processNodeForEdges(data.scene);
    return edges;
};

const AutomationBuilderCanvas = memo(({ data, onChange, onEditDetails, defaultNode }) => {
    const [nodes, setNodes] = useState(() => transformDataToNodes(data));
    const [edges, setEdges] = useState(() => transformDataToEdges(data));

    const onSidePanelChange = useCallback(
        (path, newData) => {
            const currentNode = dot.get(data, path);
            if (newData.type === 'action') {
                setNodes((prevNodes) => {
                    return prevNodes.map((node) => {
                        if (node.id === currentNode.id) {
                            return {
                                ...node,
                                name: newData.name,
                            };
                        }
                        return node;
                    });
                });
            }
            onChange(path, newData);
        },
        [data, onChange],
    );

    const onDeleteNode = useCallback(
        (id) => {
            const findNodeAndParent = (node, parentNode = null) => {
                if (node.id === id) {
                    return { nodeToDelete: node, parent: parentNode };
                }
                if (node.next && node.next.length > 0) {
                    for (const nextNode of node.next) {
                        const result = findNodeAndParent(nextNode, node);
                        if (result) return result;
                    }
                }
                return null;
            };

            const result = findNodeAndParent(data.scene);
            if (!result) return;

            const { nodeToDelete, parent } = result;
            if (parent) {
                const nodeIndex = parent.next.findIndex((n) => n.id === id);
                if (nodeIndex !== -1) {
                    const childrenNodes = nodeToDelete.next || [];
                    parent.next.splice(nodeIndex, 1, ...childrenNodes);
                }
            } else if (nodeToDelete.next && nodeToDelete.next.length > 0) {
                const newRoot = nodeToDelete.next[0];
                if (nodeToDelete.next.length > 1) {
                    newRoot.next = [...(newRoot.next || []), ...nodeToDelete.next.slice(1)];
                }
                data.scene = newRoot;
            }

            recalculateNodePositions(data.scene);

            onChange('scene', data.scene);

            setNodes((prevNodes) => {
                const newNodes = prevNodes.filter((node) => node.id !== id);
                return newNodes;
            });

            setEdges((prevEdges) => {
                const filteredEdges = prevEdges.filter(
                    (edge) => edge.source !== id && edge.target !== id,
                );

                if (parent && nodeToDelete.next) {
                    nodeToDelete.next.forEach((child) => {
                        filteredEdges.push({
                            id: `edge-${parent.id}-${child.id}`,
                            source: parent.id,
                            target: child.id,
                            type: 'buttonedge',
                        });
                    });
                }

                return filteredEdges;
            });
        },
        [data, onChange],
    );

    return (
        <AutomationBuilderCanvasProvider
            defaultNode={defaultNode}
            data={data}
            onChange={onChange}
            onDeleteNode={onDeleteNode}
            setNodes={setNodes}
            setEdges={setEdges}
        >
            <div className="fm-automation-builder__canvas">
                <div className="fm-automation-builder__canvas-inner">
                    <AutomationBuilderToolbar
                        enabled={data?.enabled || false}
                        onChange={onChange}
                        onEditDetails={onEditDetails}
                    />
                    <ReactFlowProvider>
                        <AutomationBuilderCanvasFlow
                            data={data}
                            onChange={onChange}
                            nodes={nodes}
                            edges={edges}
                            setNodes={setNodes}
                            setEdges={setEdges}
                        />
                        <NodeLayouter />
                    </ReactFlowProvider>
                </div>
                <AutomationBuilderCanvasSidePanel onChange={onSidePanelChange} data={data} />
            </div>
        </AutomationBuilderCanvasProvider>
    );
});

export default AutomationBuilderCanvas;
